import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  BookOpen, 
  Building, 
  Users, 
  Cat, 
  Calendar, 
  Receipt, 
  ChevronDown, 
  ChevronRight,
  CheckCircle,
  Lightbulb,
  HelpCircle,
  Shield,
  Activity,
  ArrowLeft,
  UserPlus,
  ClipboardList,
  Euro,
  Camera,
  Bell,
  FileText,
  Mail,
  Lock,
  Settings,
  Palmtree,
  Clock,
  AlertTriangle,
  CreditCard,
  Download,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  Check,
  X,
  User,
  Star,
  Key,
  LogIn,
  UserCheck,
  GraduationCap,
  Heart
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export const Guide = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isInstructor } = useAuth();
  
  // Determine guide type based on user role
  const getDefaultGuideType = () => {
    if (!user) return 'cavalier';
    return isInstructor() ? 'instructeur' : 'cavalier';
  };
  
  const [expandedSection, setExpandedSection] = useState('installation');
  const [guideType, setGuideType] = useState(getDefaultGuideType());
  
  const isPublic = location.pathname === '/guide-public';
  const isFirstLogin = location.state?.firstLogin === true;

  // Update guide type when user changes
  React.useEffect(() => {
    if (user) {
      setGuideType(isInstructor() ? 'instructeur' : 'cavalier');
    }
  }, [user, isInstructor]);

  // Mark guide as shown for first login
  React.useEffect(() => {
    if (isFirstLogin && user) {
      markGuideAsShown();
    }
  }, [isFirstLogin, user]);

  const markGuideAsShown = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API_URL}/api/users/me/guide-shown`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error marking guide as shown:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const Section = ({ id, icon: Icon, title, children }) => {
    const isExpanded = expandedSection === id;
    return (
      <Card className="mb-4">
        <CardHeader 
          className="cursor-pointer hover:bg-stone-50 transition-colors"
          onClick={() => toggleSection(id)}
        >
          <CardTitle className="text-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2E4A3D]/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#2E4A3D]" />
              </div>
              <span className="text-[#2E4A3D]">{title}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-stone-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-stone-400" />
            )}
          </CardTitle>
        </CardHeader>
        {isExpanded && (
          <CardContent className="pt-0 animate-fadeIn">
            {children}
          </CardContent>
        )}
      </Card>
    );
  };

  const Step = ({ number, title, children }) => (
    <div className="flex gap-4 mb-6">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#2E4A3D] text-white flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-[#2E4A3D] mb-2">{title}</h4>
        <div className="text-stone-600 text-sm space-y-2">{children}</div>
      </div>
    </div>
  );

  const SubStep = ({ children }) => (
    <div className="flex items-start gap-2 ml-4 my-1">
      <ChevronRight className="w-4 h-4 text-[#2E4A3D] flex-shrink-0 mt-0.5" />
      <span className="text-sm text-stone-600">{children}</span>
    </div>
  );

  const Tip = ({ children, type = "info" }) => {
    const styles = {
      info: "bg-blue-50 border-blue-200 text-blue-800",
      warning: "bg-amber-50 border-amber-200 text-amber-800",
      success: "bg-green-50 border-green-200 text-green-800"
    };
    const icons = {
      info: <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />,
      warning: <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />,
      success: <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
    };
    return (
      <div className={`flex items-start gap-3 p-4 border rounded-lg my-4 ${styles[type]}`}>
        {icons[type]}
        <p className="text-sm">{children}</p>
      </div>
    );
  };

  const UseCase = ({ title, icon: Icon, children }) => (
    <div className="border border-[#2E4A3D]/20 rounded-lg p-4 mb-4 bg-[#2E4A3D]/5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-[#2E4A3D]" />
        <h4 className="font-semibold text-[#2E4A3D]">{title}</h4>
      </div>
      <div className="space-y-2 text-sm text-stone-700">{children}</div>
    </div>
  );

  const ActionButton = ({ icon: Icon, label, color = "stone" }) => {
    const colors = {
      stone: "bg-stone-100 text-stone-700",
      green: "bg-green-100 text-green-700",
      blue: "bg-blue-100 text-blue-700",
      red: "bg-red-100 text-red-700",
      amber: "bg-amber-100 text-amber-700",
      purple: "bg-purple-100 text-purple-700"
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${colors[color]}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  // ========================
  // GUIDE CAVALIER
  // ========================
  const GuideCavalier = () => (
    <>
      {/* Table of contents - Cavalier */}
      <Card className="mb-6 bg-green-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-[#2E4A3D] mb-3">📖 Sommaire - Guide Cavalier</h3>
          <div className="grid md:grid-cols-2 gap-2">
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-inscription')}>
              <UserPlus className="w-4 h-4 mr-2" /> 1. S'inscrire sur Equibook
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-connexion')}>
              <LogIn className="w-4 h-4 mr-2" /> 2. Se connecter
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-dashboard')}>
              <ClipboardList className="w-4 h-4 mr-2" /> 3. Mon tableau de bord
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-cours')}>
              <Calendar className="w-4 h-4 mr-2" /> 4. M'inscrire aux cours
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-absences')}>
              <X className="w-4 h-4 mr-2" /> 5. Déclarer une absence
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-cheval')}>
              <Cat className="w-4 h-4 mr-2" /> 6. Mon cheval
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-factures')}>
              <Receipt className="w-4 h-4 mr-2" /> 7. Mes factures
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-notifications')}>
              <Bell className="w-4 h-4 mr-2" /> 8. Mes notifications
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cav-profil')}>
              <User className="w-4 h-4 mr-2" /> 9. Mon profil
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ========== SECTION 1: INSCRIPTION ========== */}
      <Section id="cav-inscription" icon={UserPlus} title="1. S'inscrire sur Equibook">
        
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">🎯 Objectif</h3>
          <p className="text-sm text-green-700">Créer votre compte cavalier pour accéder aux services du club : inscriptions aux cours, planning, factures...</p>
        </div>

        <Step number="1" title="Accéder à la page d'inscription">
          <p>Ouvrez votre navigateur et allez sur le site de votre club équestre.</p>
          <SubStep>Cliquez sur <ActionButton icon={UserPlus} label="Créer un compte" color="green" /></SubStep>
        </Step>

        <Step number="2" title="Remplir le formulaire">
          <p>Renseignez vos informations personnelles :</p>
          
          <div className="mt-3 p-3 bg-white rounded-lg border border-stone-200">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-stone-50 rounded"><strong>Prénom</strong> : Votre prénom</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Nom</strong> : Votre nom de famille</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Email</strong> : Votre email (important !)</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Téléphone</strong> : Votre numéro mobile</div>
              <div className="p-2 bg-stone-50 rounded col-span-2"><strong>Date de naissance</strong> : Pour déterminer votre catégorie d'âge</div>
            </div>
          </div>
          
          <Tip type="info">
            Votre <strong>email</strong> sera utilisé pour recevoir le lien d'activation et récupérer votre mot de passe. Assurez-vous qu'il est correct !
          </Tip>
        </Step>

        <Step number="3" title="Choisir votre club">
          <p>Sélectionnez votre club équestre dans la liste déroulante.</p>
          <SubStep>Si vous ne trouvez pas votre club, contactez l'administration</SubStep>
        </Step>

        <Step number="4" title="Répondre à la question anti-spam">
          <p>Une question simple vous sera posée (ex: "5 + 3 = ?") pour vérifier que vous n'êtes pas un robot.</p>
          <SubStep>Entrez la réponse dans le champ prévu</SubStep>
        </Step>

        <Step number="5" title="Soumettre votre inscription">
          <SubStep>Cliquez sur <ActionButton icon={Check} label="Envoyer ma demande" color="green" /></SubStep>
          <p className="mt-2">Votre demande est envoyée à l'équipe du club.</p>
          
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800">
              <strong>⏳ Délai de validation :</strong> Un instructeur examinera votre demande dans un délai maximum de <strong>72 heures</strong>.
            </p>
          </div>
        </Step>

        <Step number="6" title="Recevoir le lien d'activation">
          <p>Une fois votre compte approuvé par l'instructeur :</p>
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-2 p-2 bg-green-50 rounded">
              <Mail className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">Vous recevez un <strong>email avec un lien d'activation</strong></span>
            </div>
            <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
              <Clock className="w-5 h-5 text-blue-600" />
              <span className="text-sm text-blue-700">Ce lien est valable <strong>72 heures</strong></span>
            </div>
          </div>
          
          <Tip type="warning">
            Pensez à vérifier vos <strong>spams/courriers indésirables</strong> si vous ne voyez pas l'email !
          </Tip>
        </Step>

        <Step number="7" title="Activer votre compte">
          <SubStep>Cliquez sur le lien dans l'email</SubStep>
          <SubStep>Créez votre <strong>mot de passe</strong> (minimum 6 caractères)</SubStep>
          <SubStep>Renseignez votre adresse postale (optionnel)</SubStep>
          <SubStep>Acceptez les <strong>Conditions Générales d'Utilisation</strong></SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Check} label="Activer mon compte" color="green" /></SubStep>
          
          <Tip type="success">
            Félicitations ! Votre compte est maintenant actif. Vous pouvez vous connecter avec votre email et le mot de passe que vous venez de créer.
          </Tip>
        </Step>
      </Section>

      {/* ========== SECTION 2: CONNEXION ========== */}
      <Section id="cav-connexion" icon={LogIn} title="2. Se connecter">
        
        <Step number="1" title="Se connecter à son compte">
          <SubStep>Allez sur la page de connexion</SubStep>
          <SubStep>Entrez votre <strong>email</strong></SubStep>
          <SubStep>Entrez votre <strong>mot de passe</strong></SubStep>
          <SubStep>Cliquez sur <ActionButton icon={LogIn} label="Se connecter" color="green" /></SubStep>
        </Step>

        <Step number="2" title="Mot de passe oublié ?">
          <p>Si vous avez oublié votre mot de passe :</p>
          <SubStep>Sur la page de connexion, cliquez sur <ActionButton icon={Key} label="Mot de passe oublié ?" color="blue" /></SubStep>
          <SubStep>Entrez votre email</SubStep>
          <SubStep>Cliquez sur "Envoyer le lien"</SubStep>
          
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>📧 Vous recevrez un email</strong> avec un lien pour créer un nouveau mot de passe (valable 72h).
            </p>
          </div>
          
          <Tip type="info">
            Pensez à vérifier vos <strong>spams</strong> si vous ne recevez pas l'email.
          </Tip>
        </Step>

        <Step number="3" title="Lien d'activation expiré ?">
          <p>Si votre lien d'activation a expiré (après 72h) :</p>
          <SubStep>Contactez votre instructeur</SubStep>
          <SubStep>Il pourra vous renvoyer un nouveau lien d'activation depuis votre fiche</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 3: DASHBOARD ========== */}
      <Section id="cav-dashboard" icon={ClipboardList} title="3. Mon tableau de bord">
        
        <p className="text-stone-600 mb-4">Après connexion, vous arrivez sur votre <strong>Dashboard</strong> personnalisé.</p>
        
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">Mes cours</span>
            </div>
            <p className="text-sm text-blue-700">Voir tous mes cours à venir et passés</p>
          </div>
          
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-800">Facturation</span>
            </div>
            <p className="text-sm text-green-700">Montant dû et factures en attente</p>
          </div>
          
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Cat className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-800">Mon cheval</span>
            </div>
            <p className="text-sm text-amber-700">Accès à la fiche de votre cheval (si vous en avez un)</p>
          </div>
          
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-800">Prochains cours</span>
            </div>
            <p className="text-sm text-purple-700">Les 3 prochains cours où vous êtes inscrit</p>
          </div>
        </div>

        <Step number="1" title="Actions rapides">
          <p>Depuis le Dashboard, vous pouvez rapidement :</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <ActionButton icon={Plus} label="S'inscrire à un cours" color="green" />
            <ActionButton icon={X} label="Déclarer une absence" color="amber" />
            <ActionButton icon={Receipt} label="Voir mes factures" color="blue" />
          </div>
        </Step>
      </Section>

      {/* ========== SECTION 4: INSCRIPTION COURS ========== */}
      <Section id="cav-cours" icon={Calendar} title="4. M'inscrire aux cours">
        
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">📋 Types de cours</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Cours collectif</strong> : Groupe de plusieurs cavaliers (inscription libre si places disponibles)</li>
            <li>• <strong>Cours particulier</strong> : 1 cavalier avec 1 instructeur (contactez le club)</li>
            <li>• <strong>Cours avec groupe fixe</strong> : Réservé à un groupe spécifique (inscription par l'instructeur)</li>
          </ul>
        </div>

        <Step number="1" title="Accéder au planning">
          <SubStep>Cliquez sur <ActionButton icon={Calendar} label="Planning" /> dans le menu</SubStep>
          <SubStep>Ou depuis le Dashboard, cliquez sur "S'inscrire à un cours"</SubStep>
        </Step>

        <Step number="2" title="Trouver un cours">
          <p>Utilisez les filtres pour trouver le cours qui vous convient :</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <ActionButton icon={Filter} label="Type de cours" />
            <ActionButton icon={Filter} label="Thème (Dressage, CSO...)" />
            <ActionButton icon={Filter} label="Date" />
            <ActionButton icon={Filter} label="Instructeur" />
          </div>
          <SubStep>Les cours où il reste des places sont marqués avec le nombre disponible</SubStep>
        </Step>

        <Step number="3" title="S'inscrire à un cours collectif">
          <SubStep>Cliquez sur le cours souhaité</SubStep>
          <SubStep>Vérifiez les informations : date, heure, durée, instructeur</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="S'inscrire" color="green" /></SubStep>
          
          <Tip type="success">
            Votre inscription est immédiate ! Vous recevrez un rappel avant le cours.
          </Tip>
        </Step>

        <Step number="4" title="Règles d'inscription">
          <div className="space-y-3 mt-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <GraduationCap className="w-5 h-5 text-amber-600" />
                <span className="font-medium text-amber-800">Niveau de galop</span>
              </div>
              <p className="text-sm text-amber-700">Certains cours ont un niveau minimum requis (ex: Galop 3-4). Vous ne pouvez pas vous inscrire si votre niveau ne correspond pas.</p>
            </div>
            
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">Catégorie d'âge</span>
              </div>
              <p className="text-sm text-blue-700">Certains cours sont réservés à certaines tranches d'âge (enfants, ados, adultes).</p>
            </div>
            
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-purple-800">Cours avec groupe</span>
              </div>
              <p className="text-sm text-purple-700">Si un cours est assigné à un groupe fixe, seuls les membres du groupe peuvent s'inscrire. Contactez un instructeur pour rejoindre un groupe.</p>
            </div>
            
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <User className="w-5 h-5 text-red-600" />
                <span className="font-medium text-red-800">Cours particulier</span>
              </div>
              <p className="text-sm text-red-700">Les cours particuliers sont réservés par le club. Contactez un instructeur pour en programmer un.</p>
            </div>
          </div>
        </Step>

        <Step number="5" title="Se désinscrire">
          <SubStep>Allez sur le cours dans le planning</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={X} label="Se désinscrire" color="red" /></SubStep>
          <SubStep>Confirmez votre désinscription</SubStep>
          
          <Tip type="warning">
            Pensez à vous désinscrire <strong>au moins 24h à l'avance</strong> pour libérer la place pour un autre cavalier.
          </Tip>
        </Step>
      </Section>

      {/* ========== SECTION 5: ABSENCES ========== */}
      <Section id="cav-absences" icon={X} title="5. Déclarer une absence">
        
        <Step number="1" title="Pourquoi déclarer une absence ?">
          <ul className="text-sm text-stone-600 space-y-1">
            <li>• Libérer votre place pour un autre cavalier</li>
            <li>• Informer l'instructeur de votre absence</li>
            <li>• Garder un historique de vos présences</li>
          </ul>
        </Step>

        <Step number="2" title="Comment déclarer une absence">
          <SubStep>Allez dans <ActionButton icon={Calendar} label="Planning" /></SubStep>
          <SubStep>Trouvez le cours où vous êtes inscrit</SubStep>
          <SubStep>Cliquez sur le cours</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={X} label="Je serai absent" color="amber" /></SubStep>
          <SubStep>Optionnel : ajoutez un motif</SubStep>
          <SubStep>Confirmez</SubStep>
        </Step>

        <Tip type="info">
          Vous pouvez aussi déclarer une absence depuis le Dashboard en cliquant sur "Déclarer une absence".
        </Tip>
      </Section>

      {/* ========== SECTION 6: MON CHEVAL ========== */}
      <Section id="cav-cheval" icon={Cat} title="6. Mon cheval">
        
        <p className="text-stone-600 mb-4">Si vous êtes <strong>propriétaire</strong>, <strong>propriétaire partagé (PP)</strong> ou <strong>demi-pensionnaire (DP)</strong>, vous avez accès à la fiche de votre cheval.</p>

        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h3 className="font-semibold text-amber-800 mb-2">🐴 Règles d'attribution</h3>
          <ul className="text-sm text-amber-700 space-y-1">
            <li>• <strong>Propriétaire</strong> : Peut posséder plusieurs chevaux (pas de limite)</li>
            <li>• <strong>PP / DP / Forfait</strong> : Maximum <strong>2 chevaux</strong> par cavalier</li>
          </ul>
        </div>

        <Step number="1" title="Accéder à la fiche de mon cheval">
          <SubStep>Cliquez sur <ActionButton icon={Cat} label="Chevaux" /> dans le menu</SubStep>
          <SubStep>Votre/vos cheval(aux) s'affiche(nt) automatiquement</SubStep>
          <SubStep>Cliquez sur un cheval pour voir sa fiche complète</SubStep>
        </Step>

        <Step number="2" title="Ce que je peux voir et modifier">
          <div className="mt-3 space-y-3">
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="font-medium text-green-800">✅ Informations générales</span>
              <p className="text-sm text-green-700">Nom, race, robe, description, niveau...</p>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="font-medium text-green-800">✅ Photos</span>
              <p className="text-sm text-green-700">Ajouter, supprimer ou définir la photo principale</p>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="font-medium text-green-800">✅ Alimentation</span>
              <p className="text-sm text-green-700">Rations, précautions, intolérances...</p>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="font-medium text-green-800">✅ Santé & Soins</span>
              <p className="text-sm text-green-700">Dates des soins (maréchal, vaccins, vermifuge...)</p>
            </div>
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="font-medium text-green-800">✅ Commentaires</span>
              <p className="text-sm text-green-700">Ajouter des notes sur votre cheval</p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="font-medium text-blue-800">👀 Cavaliers associés (lecture seule)</span>
              <p className="text-sm text-blue-700">Voir qui d'autre est associé au cheval</p>
            </div>
          </div>
          
          <Tip type="info">
            <strong>Propriétaires uniquement :</strong> Vous pouvez également gérer la liste des propriétaires partagés (PP).
          </Tip>
        </Step>

        <Step number="3" title="Ajouter une photo">
          <SubStep>Ouvrez la fiche de votre cheval</SubStep>
          <SubStep>Développez la carte "Photos"</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="Ajouter" color="blue" /></SubStep>
          <SubStep>Sélectionnez une image (JPG, PNG, WEBP)</SubStep>
          <SubStep>Cliquez sur l'étoile pour définir la photo principale</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 7: MES FACTURES ========== */}
      <Section id="cav-factures" icon={Receipt} title="7. Mes factures">
        
        <Step number="1" title="Voir mes factures">
          <SubStep>Cliquez sur <ActionButton icon={Receipt} label="Facturation" /> dans le menu</SubStep>
          <SubStep>Ou depuis le Dashboard, cliquez sur "Mes factures"</SubStep>
        </Step>

        <Step number="2" title="Comprendre les statuts">
          <div className="mt-3 space-y-2">
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">En attente</span>
              <span className="text-sm text-stone-600">Facture reçue, à payer</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">Payée</span>
              <span className="text-sm text-stone-600">Règlement effectué</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-stone-100 text-stone-700 rounded text-sm font-medium">Brouillon</span>
              <span className="text-sm text-stone-600">En cours de préparation par le club</span>
            </div>
          </div>
        </Step>

        <Step number="3" title="Télécharger une facture PDF">
          <SubStep>Cliquez sur la facture pour l'ouvrir</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Download} label="Télécharger PDF" /></SubStep>
          <SubStep>Le PDF contient toutes les informations pour le paiement (IBAN, montant...)</SubStep>
        </Step>

        <Step number="4" title="Effectuer le paiement">
          <p className="mb-2">Les modes de paiement acceptés dépendent de votre club :</p>
          <ul className="text-sm text-stone-600 space-y-1">
            <li>• Virement bancaire (IBAN sur la facture)</li>
            <li>• Chèque à l'ordre du club</li>
            <li>• Espèces au secrétariat</li>
          </ul>
          
          <Tip type="info">
            Une fois le paiement reçu par le club, la facture passera automatiquement en statut "Payée".
          </Tip>
        </Step>
      </Section>

      {/* ========== SECTION 8: MES NOTIFICATIONS ========== */}
      <Section id="cav-notifications" icon={Bell} title="8. Mes notifications">
        
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">🔔 Restez informé</h3>
          <p className="text-sm text-blue-700">Recevez des notifications pour les annulations de cours, vos factures, les mises à jour de votre cheval et plus encore.</p>
        </div>

        <Step number="1" title="Consulter mes notifications">
          <p>Vos notifications sont accessibles de deux façons :</p>
          <SubStep>Cliquez sur l'icône <ActionButton icon={Bell} label="" color="blue" /> dans le menu (le badge rouge indique le nombre de non-lues)</SubStep>
          <SubStep>Ou depuis le tableau de bord, section "Notifications"</SubStep>
          <SubStep>Cliquez sur une notification pour voir les détails</SubStep>
        </Step>

        <Step number="2" title="Types de notifications">
          <p>Vous recevez automatiquement des notifications pour :</p>
          
          <div className="mt-4 space-y-2">
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-1 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" /> Cours & Stages
              </h5>
              <p className="text-sm text-stone-600">Annulations, changements d'horaire, inscriptions par l'instructeur</p>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-1 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-green-600" /> Facturation
              </h5>
              <p className="text-sm text-stone-600">Nouvelles factures, confirmations de paiement, avoirs</p>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-1 flex items-center gap-2">
                <Cat className="w-4 h-4 text-blue-600" /> Votre cheval
              </h5>
              <p className="text-sm text-stone-600">Commentaires importants, mises à jour des soins (si vous êtes propriétaire, PP ou DP)</p>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-1 flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-600" /> Groupes
              </h5>
              <p className="text-sm text-stone-600">Ajout ou retrait d'un groupe pédagogique</p>
            </div>
          </div>
        </Step>

        <Step number="3" title="Personnaliser mes préférences">
          <p>Vous pouvez choisir quelles notifications recevoir :</p>
          <SubStep>Allez dans <ActionButton icon={Settings} label="Paramètres" /></SubStep>
          <SubStep>Cliquez sur l'onglet <ActionButton icon={Bell} label="Notifications" color="blue" /></SubStep>
          <SubStep>Pour chaque type, activez/désactivez :</SubStep>
          
          <div className="ml-8 mt-2 space-y-1 text-sm text-stone-600">
            <p>• <strong>In-App</strong> : Voir la notification dans l'application</p>
            <p>• <strong>Email</strong> : Recevoir par email</p>
          </div>
          
          <Tip type="info">
            Par défaut, toutes les notifications in-app sont activées. Les emails sont désactivés pour éviter de surcharger votre boîte mail.
          </Tip>
        </Step>

        <Step number="4" title="Recevoir les emails de notification">
          <p>Pour être sûr de recevoir les emails de notification dans votre boîte de réception :</p>
          <SubStep><strong>Ajoutez l'adresse expéditeur à vos contacts</strong> : <span className="font-mono text-sm bg-stone-100 px-2 py-0.5 rounded">admin.equibook@equitek.fr</span></SubStep>
          <SubStep>Vérifiez régulièrement votre dossier "Spam" ou "Indésirables"</SubStep>
          <SubStep>Si un email y est, marquez-le comme "Non spam"</SubStep>
          
          <Tip type="warning">
            Certaines boîtes mail (Gmail, Outlook...) peuvent classer les emails automatiques en spam. L'ajout de l'adresse à vos contacts résout ce problème.
          </Tip>
        </Step>

        <Step number="5" title="Marquer comme lue / Archiver">
          <SubStep>Ouvrez une notification pour la marquer comme lue automatiquement</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Check} label="Archiver" color="stone" /> pour la retirer de la liste principale</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 9: MON PROFIL ========== */}
      <Section id="cav-profil" icon={User} title="9. Mon profil">
        
        <Step number="1" title="Modifier mes informations">
          <SubStep>Cliquez sur <ActionButton icon={Settings} label="Paramètres" /> dans le menu</SubStep>
          <SubStep>Onglet "Mon profil"</SubStep>
          <SubStep>Modifiez : prénom, nom, téléphone, pseudo...</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Check} label="Enregistrer" color="green" /></SubStep>
        </Step>

        <Step number="2" title="Changer mon mot de passe">
          <SubStep>Dans Paramètres, onglet "Sécurité"</SubStep>
          <SubStep>Entrez votre mot de passe actuel</SubStep>
          <SubStep>Entrez le nouveau mot de passe (2 fois)</SubStep>
          <SubStep>Enregistrez</SubStep>
        </Step>

        <Step number="3" title="Mon niveau de galop">
          <p>Votre niveau de galop est géré par l'instructeur. Il détermine à quels cours vous pouvez vous inscrire.</p>
          <Tip type="info">
            Contactez votre instructeur si votre niveau a changé (après passage d'un galop).
          </Tip>
        </Step>
      </Section>

      {/* FAQ Cavalier */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-[#2E4A3D]" />
            Questions fréquentes - Cavaliers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Je ne peux pas m'inscrire à un cours</p>
            <p className="text-sm text-stone-600 mt-1">Vérifiez : 1) S'il reste des places 2) Si votre niveau de galop correspond 3) Si le cours n'est pas réservé à un groupe. Si le problème persiste, contactez un instructeur.</p>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Mon lien d'activation a expiré</p>
            <p className="text-sm text-stone-600 mt-1">Contactez un instructeur pour qu'il vous renvoie un nouveau lien d'activation depuis votre fiche cavalier.</p>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Je ne vois pas mon cheval</p>
            <p className="text-sm text-stone-600 mt-1">Votre cheval doit être attribué par un instructeur. Contactez le secrétariat pour qu'ils vérifient votre liaison avec le cheval.</p>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Comment voir mes cours à venir ?</p>
            <p className="text-sm text-stone-600 mt-1">Depuis le Dashboard, cliquez sur "Mes cours" ou allez dans Planning et filtrez par "Mes cours".</p>
          </div>
        </CardContent>
      </Card>
    </>
  );

  // ========================
  // GUIDE INSTRUCTEUR (existant, mise à jour)
  // ========================
  const GuideInstructeur = () => (
    <>
      {/* Table of contents */}
      <Card className="mb-6 bg-stone-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-[#2E4A3D] mb-3">📖 Sommaire - Guide Instructeur</h3>
          <div className="grid md:grid-cols-2 gap-2">
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('installation')}>
              <Building className="w-4 h-4 mr-2" /> 1. Installation du club
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('roles')}>
              <Shield className="w-4 h-4 mr-2" /> 2. Rôles et permissions
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('cavaliers')}>
              <Users className="w-4 h-4 mr-2" /> 3. Gestion des cavaliers
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('chevaux')}>
              <Cat className="w-4 h-4 mr-2" /> 4. Gestion des chevaux
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('soins')}>
              <Activity className="w-4 h-4 mr-2" /> 5. Suivi des soins
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('planning')}>
              <Calendar className="w-4 h-4 mr-2" /> 6. Planning et cours
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('facturation')}>
              <Receipt className="w-4 h-4 mr-2" /> 7. Facturation
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('notifications')}>
              <Bell className="w-4 h-4 mr-2" /> 8. Notifications
            </Button>
            <Button variant="ghost" className="justify-start" onClick={() => setExpandedSection('usecases')}>
              <ClipboardList className="w-4 h-4 mr-2" /> 9. Cas d'usage
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ========== SECTION 1: INSTALLATION ========== */}
      <Section id="installation" icon={Building} title="1. Installation et configuration du club">
        
        <div className="mb-6 p-4 bg-[#2E4A3D]/5 rounded-lg">
          <h3 className="font-semibold text-[#2E4A3D] mb-2">Prérequis</h3>
          <ul className="text-sm text-stone-600 space-y-1">
            <li>• Un navigateur web moderne (Chrome, Firefox, Safari, Edge)</li>
            <li>• Une adresse email valide pour le compte administrateur</li>
            <li>• Les informations légales du club (SIRET, adresse, etc.)</li>
          </ul>
        </div>

        <Step number="1" title="Accéder à l'application">
          <p>Ouvrez votre navigateur et accédez à l'URL de votre application Equibook.</p>
          <SubStep>Cliquez sur le bouton vert <ActionButton icon={UserPlus} label="Créer un compte" color="green" /></SubStep>
        </Step>

        <Step number="2" title="Créer le compte administrateur">
          <p>Le <strong>premier compte créé</strong> deviendra automatiquement l'administrateur du club.</p>
          
          <div className="mt-3 p-3 bg-white rounded-lg border border-stone-200">
            <p className="font-medium text-stone-700 mb-2">Formulaire d'inscription :</p>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="p-2 bg-stone-50 rounded"><strong>Prénom</strong> : Votre prénom</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Nom</strong> : Votre nom</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Email</strong> : Email professionnel</div>
              <div className="p-2 bg-stone-50 rounded"><strong>Téléphone</strong> : Numéro de contact</div>
            </div>
          </div>
        </Step>

        <Step number="3" title="Configurer les informations du club">
          <SubStep>Cliquez sur <ActionButton icon={Settings} label="Paramètres" /> dans le menu</SubStep>
          <SubStep>Sélectionnez l'onglet <ActionButton icon={Building} label="Club" color="blue" /></SubStep>
          
          <div className="mt-4 space-y-4">
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Camera className="w-4 h-4" /> Logo du club
              </h5>
              <p className="text-sm text-stone-600">Téléchargez le logo de votre club. Il apparaîtra sur les factures et documents.</p>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Building className="w-4 h-4" /> Informations générales
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Nom du club</strong>, Email, Téléphone, Adresse</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Informations légales
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• SIRET, Forme juridique, Capital, TVA, Code APE</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <CreditCard className="w-4 h-4" /> Coordonnées bancaires
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• IBAN, BIC, Nom de la banque</li>
              </ul>
            </div>
          </div>
        </Step>

        <Step number="4" title="Définir les périodes de vacances">
          <SubStep>Paramètres → onglet <ActionButton icon={Palmtree} label="Vacances" color="amber" /></SubStep>
          <SubStep>Ajoutez toutes les périodes de vacances scolaires</SubStep>
          <Tip type="success">
            Les vacances seront automatiquement exclues lors de la création de cours hebdomadaires.
          </Tip>
        </Step>

        <Step number="5" title="Configurer les tarifs">
          <SubStep>Allez dans <ActionButton icon={Euro} label="Tarifs" /></SubStep>
          <SubStep>Renseignez les prix : Forfaits, Cours à l'unité, Pensions, Demi-pensions, Stages...</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 2: RÔLES ========== */}
      <Section id="roles" icon={Shield} title="2. Rôles et permissions">
        
        <p className="text-stone-600 mb-4">Equibook gère différents niveaux d'accès selon le rôle de l'utilisateur.</p>
        
        <div className="space-y-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-red-600" />
              <h4 className="font-semibold text-red-800">Administrateur (instructeur_admin)</h4>
            </div>
            <p className="text-sm text-red-700 mb-2">Accès complet :</p>
            <ul className="text-sm text-red-700 space-y-1">
              <li>✓ Configuration du club, tarifs, vacances</li>
              <li>✓ Gestion des utilisateurs (approbation, rôles)</li>
              <li>✓ Gestion complète des chevaux et soins</li>
              <li>✓ Création et gestion des cours</li>
              <li>✓ Facturation complète</li>
            </ul>
          </div>
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-blue-600" />
              <h4 className="font-semibold text-blue-800">Instructeur</h4>
            </div>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>✓ Gestion des cavaliers et chevaux</li>
              <li>✓ Création et gestion des cours</li>
              <li>✓ Consultation de la facturation</li>
              <li>✗ Pas d'accès aux paramètres du club</li>
            </ul>
          </div>
          
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Cat className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-800">Cavalier propriétaire</h4>
            </div>
            <ul className="text-sm text-green-700 space-y-1">
              <li>✓ Gestion complète de son cheval (sauf suppression)</li>
              <li>✓ Peut gérer les propriétaires partagés (PP)</li>
              <li>✓ Peut posséder <strong>plusieurs chevaux</strong> (pas de limite)</li>
            </ul>
          </div>
          
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-5 h-5 text-amber-600" />
              <h4 className="font-semibold text-amber-800">Cavalier PP (Propriétaire Partagé)</h4>
            </div>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>✓ Mêmes droits que le propriétaire</li>
              <li>✓ Modification des infos, photos, santé, alimentation</li>
              <li>✗ Ne peut pas modifier la liste des cavaliers associés</li>
              <li>✗ Maximum <strong>2 chevaux</strong></li>
            </ul>
          </div>
          
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Cat className="w-5 h-5 text-purple-600" />
              <h4 className="font-semibold text-purple-800">Cavalier DP (Demi-Pensionnaire)</h4>
            </div>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>✓ Mêmes droits que le propriétaire</li>
              <li>✓ Modification des infos, photos, santé, alimentation</li>
              <li>✗ Ne peut pas modifier la liste des cavaliers associés</li>
              <li>✗ Maximum <strong>2 chevaux</strong></li>
            </ul>
          </div>
          
          <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-stone-600" />
              <h4 className="font-semibold text-stone-800">Cavalier forfait / externe</h4>
            </div>
            <ul className="text-sm text-stone-700 space-y-1">
              <li>✓ Inscription aux cours, déclaration d'absences</li>
              <li>✓ Consultation de ses factures</li>
              <li>✗ Pas d'accès à la gestion des chevaux</li>
            </ul>
          </div>
        </div>
      </Section>

      {/* ========== SECTION 3: CAVALIERS ========== */}
      <Section id="cavaliers" icon={Users} title="3. Gestion des cavaliers">
        
        <Step number="1" title="Nouveau workflow d'inscription">
          <p>Le processus d'inscription a été simplifié :</p>
          
          <div className="mt-3 space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm">1</div>
              <div>
                <p className="font-medium text-blue-800">Le cavalier s'inscrit</p>
                <p className="text-sm text-blue-700">Il remplit le formulaire avec ses infos personnelles</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-amber-600 text-white flex items-center justify-center text-sm">2</div>
              <div>
                <p className="font-medium text-amber-800">Vous approuvez l'inscription</p>
                <p className="text-sm text-amber-700">Un <strong>lien d'activation</strong> est envoyé automatiquement par email</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-sm">3</div>
              <div>
                <p className="font-medium text-green-800">Le cavalier reçoit l'email</p>
                <p className="text-sm text-green-700">Avec un lien valable 72 heures</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm">4</div>
              <div>
                <p className="font-medium text-purple-800">Le cavalier active son compte</p>
                <p className="text-sm text-purple-700">En cliquant sur le lien et créant son mot de passe</p>
              </div>
            </div>
          </div>
        </Step>

        <Step number="2" title="Approuver une inscription">
          <SubStep>Allez dans <ActionButton icon={Users} label="Cavaliers" /></SubStep>
          <SubStep>Filtrez par "En attente d'approbation"</SubStep>
          <SubStep>Cliquez sur le cavalier</SubStep>
          <SubStep>Vérifiez ses informations</SubStep>
          <SubStep>Attribuez le rôle approprié</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Check} label="Approuver" color="green" /></SubStep>
          
          <Tip type="success">
            Un email avec le lien d'activation est automatiquement envoyé au cavalier. Vous n'avez rien d'autre à faire !
          </Tip>
        </Step>

        <Step number="3" title="Renvoyer un lien d'activation">
          <p>Si le lien a expiré ou si le cavalier ne l'a pas reçu :</p>
          <SubStep>Ouvrez la fiche du cavalier approuvé</SubStep>
          <SubStep>Section "Gestion administrative"</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Mail} label="Renvoyer le lien d'activation" color="amber" /></SubStep>
          
          <Tip type="info">
            Le nouveau lien sera valable 72 heures. Conseillez au cavalier de vérifier ses spams.
          </Tip>
        </Step>

        <Step number="4" title="Attribuer des chevaux depuis la fiche cavalier">
          <p>Pour les cavaliers <strong>PP, DP ou Forfait</strong>, vous pouvez attribuer des chevaux directement depuis leur fiche :</p>
          
          <SubStep>Ouvrez la fiche du cavalier</SubStep>
          <SubStep>Section "Chevaux associés"</SubStep>
          <SubStep>Sélectionnez un cheval dans le menu déroulant</SubStep>
          
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="font-medium text-amber-800 mb-2">Règles d'attribution :</p>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• <strong>Cavalier PP</strong> → Cheval de propriétaire ou "inconnu" uniquement</li>
              <li>• <strong>Cavalier DP/FF</strong> → Cheval du club uniquement</li>
              <li>• <strong>Maximum 2 chevaux</strong> par cavalier PP/DP/FF</li>
            </ul>
          </div>
          
          <Tip type="info">
            Les propriétaires sont liés à leur cheval via la fiche du cheval, pas depuis la fiche cavalier.
          </Tip>
        </Step>

        <Step number="5" title="Définir le niveau de galop">
          <SubStep>Ouvrez la fiche du cavalier</SubStep>
          <SubStep>Trouvez le champ "Niveau Galop"</SubStep>
          <SubStep>Sélectionnez le niveau (1 à 8)</SubStep>
          
          <Tip type="warning">
            Le niveau de galop détermine à quels cours le cavalier peut s'inscrire. Vérifiez-le après chaque passage de galop !
          </Tip>
        </Step>
      </Section>

      {/* ========== SECTION 4: CHEVAUX ========== */}
      <Section id="chevaux" icon={Cat} title="4. Gestion des chevaux">
        
        <Step number="1" title="Cheval du club vs Cheval privé">
          <div className="grid md:grid-cols-2 gap-4 mt-3">
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-5 h-5 text-purple-600" />
                <span className="font-medium text-purple-800">Cheval du club</span>
              </div>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Pour les cours collectifs</li>
                <li>• <strong>Pas de facturation</strong> pension</li>
                <li>• Cavaliers DP/FF peuvent être attribués</li>
              </ul>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">Cheval privé (propriétaire)</span>
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• A un propriétaire désigné</li>
                <li>• <strong>Pension facturée</strong> mensuellement</li>
                <li>• Cavaliers PP peuvent être attribués</li>
              </ul>
            </div>
          </div>
        </Step>

        <Step number="2" title="Carte Cavaliers associés">
          <p>Cette carte permet de gérer les liens cheval-cavalier :</p>
          
          <div className="mt-3 space-y-2">
            <div className="p-2 bg-blue-50 rounded flex items-center gap-2">
              <User className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-700"><strong>Propriétaire</strong> : Seul l'instructeur peut le modifier</span>
            </div>
            <div className="p-2 bg-amber-50 rounded flex items-center gap-2">
              <Users className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700"><strong>Propriétaires Partagés (PP)</strong> : Instructeur + Propriétaire peuvent les gérer</span>
            </div>
            <div className="p-2 bg-purple-50 rounded flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-700"><strong>Demi-Pensionnaires (DP)</strong> : Instructeur uniquement</span>
            </div>
          </div>
          
          <Tip type="info">
            Les cavaliers PP et DP peuvent <strong>voir</strong> cette carte mais ne peuvent pas la modifier.
          </Tip>
        </Step>

        <Step number="3" title="Permissions des cavaliers sur leur cheval">
          <p>Les propriétaires, PP et DP ont les <strong>mêmes droits</strong> sur le cheval :</p>
          
          <ul className="mt-2 text-sm text-stone-600 space-y-1">
            <li>✅ Modifier les informations générales</li>
            <li>✅ Gérer les photos</li>
            <li>✅ Modifier l'alimentation</li>
            <li>✅ Modifier la santé & soins</li>
            <li>✅ Ajouter des commentaires</li>
            <li>❌ Modifier les cavaliers associés (sauf propriétaire pour les PP)</li>
            <li>❌ Supprimer le cheval (instructeur uniquement)</li>
          </ul>
        </Step>
      </Section>

      {/* ========== SECTION 5: SOINS ========== */}
      <Section id="soins" icon={Activity} title="5. Suivi des soins">
        
        <Step number="1" title="Accéder au tableau de bord">
          <SubStep>Cliquez sur <ActionButton icon={Activity} label="Suivi Chevaux" /></SubStep>
          <SubStep>Vue Tableau ou Vue Calendrier</SubStep>
        </Step>

        <Step number="2" title="Types de soins suivis">
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
            <div className="p-2 bg-green-50 rounded text-center">Maréchal</div>
            <div className="p-2 bg-blue-50 rounded text-center">Vaccins</div>
            <div className="p-2 bg-amber-50 rounded text-center">Vermifuge</div>
            <div className="p-2 bg-purple-50 rounded text-center">Dentiste</div>
            <div className="p-2 bg-red-50 rounded text-center">Ostéopathe</div>
            <div className="p-2 bg-stone-50 rounded text-center">Vétérinaire</div>
          </div>
        </Step>

        <Step number="3" title="Configurer les fréquences">
          <SubStep>Cliquez sur un cheval</SubStep>
          <SubStep>Pour chaque soin : dernière date + fréquence</SubStep>
          <SubStep>La prochaine date est calculée automatiquement</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 6: PLANNING ========== */}
      <Section id="planning" icon={Calendar} title="6. Planning et gestion des cours">
        
        <Step number="1" title="Créer un cours">
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="Nouveau cours" color="green" /></SubStep>
          <SubStep>Renseignez : Type, Thème, Date, Durée, Places, Instructeur</SubStep>
          <SubStep>Optionnel : Niveau de galop requis, Catégorie d'âge, Groupe</SubStep>
        </Step>

        <Step number="2" title="Règles d'inscription aux cours">
          <div className="mt-3 space-y-2">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="font-medium text-blue-800">Cours collectif sans groupe</span>
              <p className="text-sm text-blue-700">Les cavaliers peuvent s'inscrire librement (si niveau OK)</p>
            </div>
            <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
              <span className="font-medium text-purple-800">Cours avec groupe fixe</span>
              <p className="text-sm text-purple-700">Seuls les membres du groupe peuvent s'inscrire</p>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <span className="font-medium text-amber-800">Cours particulier</span>
              <p className="text-sm text-amber-700">Places max = 1 automatiquement, inscription par l'instructeur</p>
            </div>
          </div>
        </Step>

        <Step number="3" title="Création en masse">
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="Création en masse" color="blue" /></SubStep>
          <SubStep>Définissez le jour, l'heure, la période</SubStep>
          <SubStep>Les vacances sont automatiquement exclues</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 7: FACTURATION ========== */}
      <Section id="facturation" icon={Receipt} title="7. Facturation">
        
        <Step number="1" title="Générer des factures">
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="Générer des factures" color="green" /></SubStep>
          <SubStep>Sélectionnez le type : Pensions, Forfaits, Demi-pensions</SubStep>
          <SubStep>Choisissez la période</SubStep>
        </Step>

        <Step number="2" title="Créer un avoir">
          <SubStep>Ouvrez une facture</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Receipt} label="Créer un avoir" color="amber" /></SubStep>
          
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="font-medium text-blue-800 mb-2">Logique automatique :</p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Facture <strong>payée</strong> → Avoir "En attente" (remboursement à effectuer)</li>
              <li>• Facture <strong>impayée</strong> → Avoir "Soldé" (pas de remboursement)</li>
            </ul>
          </div>
        </Step>

        <Step number="3" title="Télécharger et envoyer">
          <SubStep>Ouvrez la facture</SubStep>
          <SubStep><ActionButton icon={Download} label="Télécharger PDF" /> ou <ActionButton icon={Mail} label="Envoyer" color="blue" /></SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 8: NOTIFICATIONS ========== */}
      <Section id="notifications" icon={Bell} title="8. Notifications">
        
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="font-semibold text-blue-800 mb-2">📬 Système de notifications</h3>
          <p className="text-sm text-blue-700">Equibook dispose d'un système de notifications automatiques et manuelles pour communiquer avec vos cavaliers. Les cavaliers peuvent personnaliser leurs préférences de réception.</p>
        </div>

        <Step number="1" title="Notifications automatiques">
          <p>Certaines notifications sont envoyées <strong>automatiquement</strong> par le système :</p>
          
          <div className="mt-4 space-y-3">
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-purple-600" /> Cours & Stages
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Annulation/Suppression</strong> : Les cavaliers inscrits sont notifiés avec le motif</li>
                <li>• <strong>Modification horaire</strong> : Notification si l'heure ou la date change</li>
                <li>• <strong>Inscription par l'instructeur</strong> : Le cavalier est notifié quand vous l'inscrivez</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-green-600" /> Facturation
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Nouvelle facture</strong> : Notification à l'envoi de la facture</li>
                <li>• <strong>Facture payée</strong> : Confirmation de paiement enregistré</li>
                <li>• <strong>Avoir créé</strong> : Notification lors de la création d'un avoir</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Euro className="w-4 h-4 text-amber-600" /> Tarifs
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Nouveau barème</strong> : Tous les cavaliers sont informés</li>
                <li>• <strong>Modification des tarifs</strong> : Notification si les prix changent</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Cat className="w-4 h-4 text-blue-600" /> Chevaux
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Commentaire "Attention"/"À surveiller"</strong> : Propriétaire, PP et DP notifiés</li>
                <li>• <strong>Mise à jour soins</strong> : Notification aux cavaliers liés au cheval</li>
              </ul>
            </div>
            
            <div className="p-3 bg-white rounded-lg border border-stone-200">
              <h5 className="font-medium text-stone-700 mb-2 flex items-center gap-2">
                <Users className="w-4 h-4 text-purple-600" /> Groupes
              </h5>
              <ul className="text-sm text-stone-600 space-y-1">
                <li>• <strong>Ajout au groupe</strong> : Le cavalier est notifié de son ajout</li>
                <li>• <strong>Retrait du groupe</strong> : Notification de retrait</li>
              </ul>
            </div>
          </div>
          
          <Tip type="info">
            Les titres des notifications de cours incluent le <strong>type d'activité</strong> (Dressage, CSO...), le <strong>groupe</strong> et la <strong>date</strong>. Exemple : "Dressage Groupe adultes du Jeudi du jeudi 15 janvier annulé"
          </Tip>
        </Step>

        <Step number="2" title="Préférences des cavaliers">
          <p>Chaque cavalier peut personnaliser ses notifications dans <ActionButton icon={Settings} label="Paramètres" /> → <ActionButton icon={Bell} label="Notifications" color="blue" /></p>
          
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <h5 className="font-medium text-amber-800 mb-2">Options disponibles par domaine :</h5>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• <strong>In-App</strong> : Notification visible dans l'application (cloche)</li>
              <li>• <strong>Email</strong> : Réception par email</li>
            </ul>
            <p className="text-xs text-amber-600 mt-2">Les cavaliers peuvent activer/désactiver chaque type de notification selon leurs préférences.</p>
          </div>
        </Step>

        <Step number="3" title="Emails et spam">
          <p>Les emails de notification peuvent être classés en spam par certaines boîtes mail. Conseillez à vos cavaliers de :</p>
          <SubStep><strong>Ajouter l'adresse expéditeur à leurs contacts</strong> : <span className="font-mono text-sm bg-stone-100 px-2 py-0.5 rounded">admin.equibook@equitek.fr</span></SubStep>
          <SubStep>Vérifier leur dossier "Spam" ou "Indésirables"</SubStep>
          <SubStep>Marquer les emails Equibook comme "Non spam" si nécessaire</SubStep>
          
          <Tip type="warning">
            Cette information est communiquée automatiquement lors de l'inscription, mais vous pouvez la rappeler à vos cavaliers en cas de problème de réception.
          </Tip>
        </Step>

        <Step number="4" title="Notifications manuelles (Éditeur)">
          <p>Pour envoyer une notification personnalisée :</p>
          <SubStep>Menu → <ActionButton icon={Bell} label="Notifications" color="blue" /></SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Plus} label="Nouvelle notification" color="green" /></SubStep>
          <SubStep>Rédigez le titre et le contenu</SubStep>
          <SubStep>Choisissez les destinataires (critères de ciblage)</SubStep>
          <SubStep>Cliquez sur <ActionButton icon={Mail} label="Publier" color="blue" /></SubStep>
          
          <div className="mt-4 p-3 bg-white rounded-lg border border-stone-200">
            <h5 className="font-medium text-stone-700 mb-2">Critères de ciblage disponibles :</h5>
            <ul className="text-sm text-stone-600 space-y-1">
              <li>• <strong>Catégorie d'âge</strong> : Adultes, Ados, Enfants, Tout-petits</li>
              <li>• <strong>Groupes pédagogiques</strong> : Cibler un ou plusieurs groupes</li>
              <li>• <strong>Cavaliers d'un cheval</strong> : Propriétaire, PP, DP d'un cheval</li>
            </ul>
          </div>
          
          <Tip type="warning">
            Les notifications manuelles ne sont <strong>pas</strong> soumises aux préférences des cavaliers. Elles seront toujours envoyées in-app et par email si configuré.
          </Tip>
        </Step>

        <Step number="5" title="Notification contextuelle rapide">
          <p>Depuis certains écrans, vous pouvez envoyer une notification contextuelle avec le bouton <ActionButton icon={Bell} label="" color="blue" /> :</p>
          
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-stone-500" />
              <span><strong>Planning</strong> : Notifier les inscrits d'un cours</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Receipt className="w-4 h-4 text-stone-500" />
              <span><strong>Factures</strong> : Relancer un cavalier pour une facture</span>
            </div>
          </div>
          
          <Tip type="success">
            La notification contextuelle est pré-remplie avec les informations du contexte (cours, facture...). Vous pouvez la personnaliser avant envoi.
          </Tip>
        </Step>

        <Step number="6" title="Consulter l'historique">
          <p>Pour voir les notifications envoyées :</p>
          <SubStep>Menu → <ActionButton icon={Bell} label="Notifications" color="blue" /></SubStep>
          <SubStep>L'onglet "Envoyées" affiche l'historique</SubStep>
          <SubStep>Cliquez sur une notification pour voir les détails et destinataires</SubStep>
        </Step>
      </Section>

      {/* ========== SECTION 9: CAS D'USAGE ========== */}
      <Section id="usecases" icon={ClipboardList} title="9. Cas d'usage pratiques">
        
        <UseCase title="Comment approuver un nouveau cavalier ?" icon={UserCheck}>
          <ol className="list-decimal list-inside space-y-2">
            <li>Cavaliers → Filtrer "En attente"</li>
            <li>Cliquez sur le cavalier</li>
            <li>Vérifiez ses infos et attribuez le rôle</li>
            <li>Cliquez sur <ActionButton icon={Check} label="Approuver" color="green" /></li>
            <li>Notez le mot de passe temporaire affiché</li>
            <li>Communiquez-le au cavalier (email/SMS/en personne)</li>
          </ol>
        </UseCase>

        <UseCase title="Comment attribuer un cheval à un cavalier DP ?" icon={Cat}>
          <ol className="list-decimal list-inside space-y-2">
            <li>Méthode 1 - Depuis la fiche cavalier :
              <ul className="list-disc list-inside ml-4 text-stone-500">
                <li>Ouvrez la fiche du cavalier DP</li>
                <li>Section "Chevaux associés"</li>
                <li>Sélectionnez un cheval du club</li>
              </ul>
            </li>
            <li>Méthode 2 - Depuis la fiche cheval :
              <ul className="list-disc list-inside ml-4 text-stone-500">
                <li>Ouvrez la fiche du cheval du club</li>
                <li>Carte "Cavaliers associés"</li>
                <li>Ajoutez le cavalier comme DP</li>
              </ul>
            </li>
          </ol>
          <Tip type="warning">
            Les cavaliers DP/FF ne peuvent avoir que <strong>2 chevaux maximum</strong>.
          </Tip>
        </UseCase>

        <UseCase title="Comment créer une saison de cours ?" icon={Calendar}>
          <ol className="list-decimal list-inside space-y-2">
            <li>D'abord : Paramètres → Vacances (ajoutez toutes les vacances)</li>
            <li>Planning → Création en masse</li>
            <li>Configurez : jour, heure, durée, type</li>
            <li>Optionnel : niveau de galop requis</li>
            <li>Définissez la période (septembre → juin)</li>
            <li>Générez les cours</li>
          </ol>
        </UseCase>
      </Section>

      {/* FAQ */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-3">
            <HelpCircle className="w-5 h-5 text-[#2E4A3D]" />
            Questions fréquentes
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Un cavalier n'a pas reçu son mot de passe</p>
            <p className="text-sm text-stone-600 mt-1">Vous pouvez régénérer un mot de passe temporaire depuis sa fiche en cliquant sur "Réinitialiser le mot de passe".</p>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Un cavalier ne peut pas s'inscrire à un cours</p>
            <p className="text-sm text-stone-600 mt-1">Vérifiez : 1) Son niveau de galop 2) Sa catégorie d'âge 3) Si le cours a un groupe assigné 4) S'il reste des places.</p>
          </div>
          <div className="p-3 bg-stone-50 rounded-lg">
            <p className="font-medium text-[#2E4A3D]">Le logo du club disparaît</p>
            <p className="text-sm text-stone-600 mt-1">Ce bug a été corrigé. Si le problème persiste, re-téléchargez le logo dans Paramètres → Club.</p>
          </div>
        </CardContent>
      </Card>
    </>
  );

  return (
    <div className={`animate-fadeIn ${isPublic ? 'min-h-screen bg-[#FDFDFC] py-8 px-4' : ''}`} data-testid="guide-page">
      <div className="max-w-4xl mx-auto">
        {isPublic && (
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-stone-600 hover:text-[#2E4A3D] mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            Retour à l'accueil
          </button>
        )}
        
        {/* Welcome message for first login */}
        {isFirstLogin && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800">
                  Bienvenue {user?.prenom} ! 🎉
                </h3>
                <p className="text-sm text-green-700">
                  Votre compte est maintenant actif. Prenez quelques minutes pour découvrir les fonctionnalités d'Equibook.
                </p>
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <Button 
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="text-green-700 border-green-300 hover:bg-green-100"
              >
                Aller au tableau de bord
              </Button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#2E4A3D] flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-serif font-bold text-[#2E4A3D]">
                {isInstructor() ? 'Guide Instructeur' : 'Guide Cavalier'}
              </h1>
              <p className="text-stone-500">
                {isInstructor() 
                  ? 'Tutoriel complet pour gérer votre club équestre' 
                  : 'Tutoriel pour utiliser l\'application'}
              </p>
            </div>
          </div>
        </div>

        {/* Guide Type Selector - Only show for instructors or public */}
        {(isInstructor() || isPublic) ? (
          <Tabs value={guideType} onValueChange={setGuideType} className="mb-6">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="cavalier" className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Guide Cavalier
              </TabsTrigger>
              <TabsTrigger value="instructeur" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Guide Instructeur
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="cavalier" className="mt-6">
              <GuideCavalier />
            </TabsContent>
            
            <TabsContent value="instructeur" className="mt-6">
              <GuideInstructeur />
            </TabsContent>
          </Tabs>
        ) : (
          /* Direct display for cavaliers - no tabs */
          <div className="mt-6">
            <GuideCavalier />
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-stone-500">
          <p>© 2025 Equibook - Gestion de club équestre simplifiée</p>
          {isPublic && (
            <Button 
              onClick={() => navigate('/register')}
              className="btn-primary mt-6"
            >
              Créer mon compte
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
